import { cn } from "@/lib/utils";
import { useI18N } from "common/hooks";
import { OpeningHour } from "common/types";
import { Clock } from "phosphor-react";

interface OpeningHoursProps {
  openingHours?: OpeningHour[];
  containerClassName?: string;
  className?: string;
  variant?: "simple" | "detailed"; // Default"simple"
}

const OpeningHours = ({
  openingHours,
  containerClassName,
  className,
  variant = "simple", // Default to simple for backward compatibility
}: OpeningHoursProps) => {
  const i18n = useI18N();

  const getTodaysOpeningHours = () => {
    const today = new Date().getDay(); // Gets today's day of the week as a number
    const openingHoursToday = openingHours?.find((hour) => hour.day === today);

    if (openingHoursToday) {
      const openTime = formatTime(openingHoursToday.openTime);
      const closeTime = formatTime(openingHoursToday.closeTime);
      return `${openTime} - ${closeTime}`;
    }
    return i18n.get("closedToday");
  };

  const formatTime = (time: any) => {
    if (typeof time !== "number" || isNaN(time)) {
      return i18n.get("invalidTime"); // Returns'Invalid Time' if the input is not a valid number
    }

    const hours = Math.floor(time / 100);
    const minutes = time % 100;
    const amPm = hours >= 12 ? "PM" : "AM";
    const formattedHour = hours % 12 || 12;

    // Ensure minutes are always two digits
    const formattedMinutes = minutes < 10 ? "0" + minutes : minutes.toString();

    return `${formattedHour}:${formattedMinutes} ${amPm}`;
  };

  // Simple variant: inline span (matches old top-level component)
  if (variant === "simple") {
    return (
      <span className="label-s-medium !text-fg-brand-subtle">
        Open • <span className={className}>{getTodaysOpeningHours()}</span>
      </span>
    );
  }

  // Detailed variant: div with icon (current Snippet version)
  return (
    <div
      className={cn(
        containerClassName,
        "flex flex-row items-center truncate text-ellipsis",
      )}
    >
      <div>
        <Clock size={24} className="!text-fg-brand-subtle" aria-hidden="true" />
      </div>
      <p className={cn(className, "ml-1")} aria-live="polite">
        {`Hours: ${getTodaysOpeningHours()}`}
      </p>
    </div>
  );
};

export default OpeningHours;
